import React, {useRef, useState} from "react";
import {Button} from "antd";
import {CloseOutlined, FileZipOutlined, InboxOutlined} from "@ant-design/icons";
import {AnimatePresence, motion} from "framer-motion";
import {formatBytes, platformTypeToAcceptedFileTypeExtension, platformTypeToName} from "../../services/Helpers";
import PlatformType from "../../types/PlatformType";
import styles from "./ApplicationUpload.module.css";

interface ApplicationUploadProps {
    onSelect: (file?: File) => void
    platformType?: PlatformType
    /** Edit mode: the existing build stays unless a new file is chosen. */
    hasExistingFile?: boolean
}

function acceptsFile(file: File, platform?: PlatformType) {
    if (!platform) return true;
    const accepted = platformTypeToAcceptedFileTypeExtension(platform).split(",");
    const name = file.name.toLowerCase();
    return accepted.some(ext => name.endsWith(ext.trim()));
}

export default function ApplicationUpload({onSelect, platformType, hasExistingFile}: ApplicationUploadProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File>();
    const [dragging, setDragging] = useState(false);
    const [rejection, setRejection] = useState<string>();

    const accepted = platformType ? platformTypeToAcceptedFileTypeExtension(platformType) : undefined;

    const take = (candidate?: File) => {
        if (!candidate) return;
        if (!acceptsFile(candidate, platformType)) {
            setRejection(`${candidate.name} isn't a ${platformTypeToName(platformType as PlatformType)} build. Use ${accepted?.split(",").join(", ")}.`);
            return;
        }
        setRejection(undefined);
        setFile(candidate);
        onSelect(candidate);
    };

    const clear = () => {
        setFile(undefined);
        onSelect(undefined);
    };

    return (
        <div>
            <input
                type="file"
                ref={inputRef}
                className="display-none"
                accept={accepted}
                onChange={event => {
                    take(event.target.files?.[0]);
                    event.target.value = "";
                }}
            />

            <AnimatePresence initial={false} mode="wait">
                {file ? (
                    <motion.div
                        key="chip"
                        className={styles.chip}
                        initial={{opacity: 0, y: 4}}
                        animate={{opacity: 1, y: 0}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.16}}
                    >
                        <FileZipOutlined className={styles.chipIcon} aria-hidden/>
                        <div className={styles.chipText}>
                            <span className={styles.chipName}>{file.name}</span>
                            <span className={`${styles.chipSize} mono`}>{formatBytes(file.size)}</span>
                        </div>
                        <Button type="text" icon={<CloseOutlined/>} onClick={clear} aria-label={`Remove ${file.name}`}/>
                    </motion.div>
                ) : (
                    <motion.button
                        key="zone"
                        type="button"
                        className={`${styles.zone} ${dragging ? styles.zoneActive : ""}`}
                        onClick={() => inputRef.current?.click()}
                        onDragOver={event => {
                            event.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={event => {
                            event.preventDefault();
                            setDragging(false);
                            take(event.dataTransfer.files?.[0]);
                        }}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.16}}
                    >
                        <InboxOutlined className={styles.zoneIcon} aria-hidden/>
                        <span className={styles.zoneTitle}>
                            {hasExistingFile ? "Drop a new build file to replace the current one" : "Drop the build file here"}
                        </span>
                        <span className={styles.zoneHint}>
                            or <span className={styles.zoneLink}>browse your computer</span>
                            {accepted ? <> · accepts <span className="mono">{accepted.split(",").join(" ")}</span></> : " · pick a platform first to see accepted files"}
                        </span>
                    </motion.button>
                )}
            </AnimatePresence>

            {rejection && <p className={styles.rejection} role="alert">{rejection}</p>}
        </div>
    );
}
